import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Coins } from "lucide-react";
import { uiText } from "@/lib/i18n/uiText";
import { useAuth } from "@/hooks/useAuth";
import { useBuyCredits } from "@/hooks/useCoins";
import { toast } from "sonner";

/** Active credit packs (public catalogue). Purchases happen in the wallet. */
export function CreditPacks() {
  const { user } = useAuth();
  const buy = useBuyCredits();
  const { data: packs = [] } = useQuery({
    queryKey: ["credit-packs-public"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("credit_packs")
        .select("id,name,credits,amount_usd")
        .eq("active", true)
        .order("sort_order");
      if (error) throw error;
      return data ?? [];
    },
    staleTime: 10 * 60 * 1000,
  });
  if (!packs.length) return null;
  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Coins className="h-5 w-5 text-primary" /> {uiText("Credit packs for agents and bots")}
        </CardTitle>
      </CardHeader>
      <CardContent className="grid gap-3 sm:grid-cols-3">
        {packs.map((p) => (
          <div key={p.id} className="flex flex-col rounded-lg border p-4">
            <p className="font-semibold">{p.name}</p>
            <p className="text-2xl font-bold">{p.credits} <span className="text-sm font-normal text-muted-foreground">{uiText("credits")}</span></p>
            <p className="mb-3 text-muted-foreground">${((p.amount_usd ?? 0) / 100).toFixed(2)}</p>
            {user ? <Button className="mt-auto" size="sm" disabled={buy.isPending}
              onClick={async () => {
                try { await buy.mutateAsync(p.id); }
                catch (error) { toast.error(uiText("Couldn't start checkout"), { description: error instanceof Error ? error.message : undefined }); }
              }}>{uiText(buy.isPending ? "Redirecting…" : "Buy credits")}</Button> :
              <Button asChild className="mt-auto" size="sm"><a href="/auth?next=%2Fai-access">{uiText("Sign in")}</a></Button>}
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
